<script>
import { defineComponent, markRaw } from 'vue'
import AgGridMethods from '@/Mixins/SigmaClientNew/AgGridMethods'
import AgGridAccountsPayableColumns from '@/Mixins/SigmaClientNew/AgGridAccountsPayableColumns'
import { AgGridVue } from 'ag-grid-vue3'

export default defineComponent({
  name: 'NachaTransactionsList',

  components: {
    AgGridVue
  },
  mixins: [ AgGridMethods, AgGridAccountsPayableColumns ],

  props: {
    // Name of input field.  Not really necessary, but helpful if we use the form object down the road
    name: {
      required: true,
      type: String
    },

    iteration: {
      required: true,
      type: Number
    },

    width: {
      required: true,
      type: String
    },

    height: {
      required: true,
      type: String
    },

    classes: {
      required: false,
      type: String,
      default: 'ag-theme-balham'
    },

    rowData: {
      required: false
    },

    // Ag-grid column defs
    columns: {
      required: false,
      type: Array,
      default: undefined
    },

    // Ag-grid Options
    options: {
      required: false,
      type: Object,
      default: undefined
    },

    // NACHA format.  Typically a bank, e.g. 'CNB' or 'CHASE'
    format: {
      required: true,
      type: String
      // default: undefined
    }
  },

  data () {
    return {
      isError: false, // If the input or selected field is incorrect, show an error
      isLoading: false,
      scope: markRaw(this),
      gridApi: null,
      columnApi: null,
      defaultColDefs: undefined,
      defaultGridOptions: undefined,
      changes: [],
      achErrors: []
    }
  },

  computed: {
    columnDefs () {
      if (this.columns && Array.isArray(this.columns)) {
        return this.columns
      } else {
        return this.defaultColDefs
      }
    },
    gridOptions () {
      return this.options || this.defaultGridOptions
    },

    // Any groups with at least one transaction selected
    selectedGroups () {
      let aggregatedNodes = []
      this.gridApi.forEachNode((node) => {
        if (node.aggData) {
          const selected = node.allLeafChildren.find((child) => child.selected)
          if (selected) aggregatedNodes.push(node)
        }
      })
      return aggregatedNodes
    },

    // Since we are now sending invoices grouped by invoice, this helper makes each group look like a transaction
    simulatedTransactionData () {
      if (!this.selectedGroups.length) return []

      let transactions = []
      this.selectedGroups.forEach((group) => {
        if (group.level < 1) return
        const data = group.aggData
        const transaction = {
          attributes: {
            vendor: data.vendorResource.id,
            vendor_name: data.vendorResource.attributes.name,
            email: data.vendorResource.attributes.email,
            routing: data.vendorResource.attributes.ach_routing_number,
            account: data.vendorResource.attributes.ach_account_number,
            amount_decimal: data.selected_amount_decimal,
            description: group.allLeafChildren ? group.allLeafChildren.map((child) => child.data.attributes.number).join(', ') : '',
            payment_confirmation_number: group.allLeafChildren ? [...new Set(group.allLeafChildren.map((child) => child.data.attributes.payment_confirmation_number))].join(', ') : '',
            bank_transaction_number: group.allLeafChildren ? (group.allLeafChildren.find((child) => child.data.attributes.bank_transaction_number) || { data: { attributes: { bank_transaction_number: '' } } }).data.attributes.bank_transaction_number : '',
            resources: group.allLeafChildren ? group.allLeafChildren.filter((child) => child.selected).map((child) => child.data) : null,
            payment_date: data.payment_date
          }
        }
        transactions.push(transaction)
      })

      return transactions
    }
  },

  methods: {
    hasCompleteAchData (vendorResource) {
      if (!vendorResource || !vendorResource.attributes) return false
      const name = vendorResource.get('name')
      const routing = vendorResource.get('ach_routing_number')
      const account = vendorResource.get('ach_account_number')
      const payeeName = vendorResource.get('payee_name')
      let routingLength = true
      let error = null
      if (!routing && !account && (payeeName === '' || payeeName === null)) {
        error = `Missing Routing, Account numbers and Payee name for vendor ${name}`
      } else if (!routing && !account && payeeName) {
        error = `Missing Routing, Account numbers for vendor ${name}`
      } else if (routing && !account && (payeeName === '' || payeeName === null)) {
        error = `Missing Account number and Payee name for vendor ${name}`
      } else if (!routing && account && (payeeName === '' || payeeName === null)) {
        error = `Missing Routing number and Payee name for vendor ${name}`
      } else if (!routing && account && payeeName) {
        error = `Missing Routing number for vendor ${name}`
      } else if (routing && !account && payeeName) {
        error = `Missing Account number for vendor ${name}`
      } else if (routing && account && (payeeName === '' || payeeName === null)) {
        error = `Missing Payee name for vendor ${name}`
      } else if (routing && account && payeeName && routing.length !== 9) {
        routingLength = false
        error = `Routing Number must be 9 characters for vendor ${name}`
      }
      if (error) {
        this.achErrors.push({name: name, message: error})
      }
      return (routing && account && payeeName && routingLength)
    },

    updateTotals (gridApi) {
      const api = gridApi || this.gridApi
      if (!api) return

      let sum = 0
      let selected = 0
      api.forEachNode((row) => {
        if (row && row.data && row.data.attributes) {
          sum += parseFloat(row.data.attributes.amount_decimal || '0')
          if (row.selected) {
            selected += parseFloat(row.data.attributes.amount_decimal || '0')
          }
        }
      })
      this.$emit('update-totals', {
        totalAvailable: sum.toFixed(2),
        totalSelected: selected.toFixed(2)
      })
    },

    updateRows (id, obj) {
      this.$emit('update-rows', { transaction: id, data: obj })
    },

    // For this, the real node/row is selectable if the routing and bank account exists on the vendor
    isRowSelectable (params) {
      if (params.data && this.hasCompleteAchData(params.data.get('vendor'))) {
        return true
      } else if (params.node && params.node.allChildrenCount) {
        return params.node.allLeafChildren.every((child) => {
          return this.hasCompleteAchData(child.data.get('vendor'))
        })
      } else {
        return false
      }
    },

    // Ag-grid
    onGridReady (params) {
      if (!params) return
      this.gridApi = params.api
      this.columnApi = params.api
      params.api.forEachNode((node) => node.setSelected(true))
    },

    onFirstDataRendered (params) {
      if (!params) return
      this.safeSizeColumnsToFit(params)
      this.updateTotals(params.api)
      if (this.achErrors.length) {
        this.$emit('ach-errors', this.achErrors)
      }
    },

    onCellValueChanged (params) {
      // Ignore if not changed
      if ((params.value && params.oldValue === params.newValue) || !params.data) return
    },

    onChangeVisibleColumns () {
      this.$nextTick(() => {
        this.gridApi.sizeColumnsToFit()
      })
    },

    exportToExcel () {
      this.gridApi.exportDataAsExcel()
    }
  },
  beforeMount () {
    this.defaultColDefs = [
      this.nachaVendorGroupColumn,
      this.apVendorRoutingColumn,
      this.apVendorAccountColumn,
      this.apNumberColumn,
      this.apBatchColumn,
      this.apDescriptionColumn,
      this.apAmountColumn,
      this.nachaSelectedAmountColumn,
      this.apPaymentNumberColumn,
      this.apVendorResource,
      this.apPaymentDateColumn,
      this.apPaymentCombineCheckColumn
    ]

    this.defaultGridOptions = {
      singleClickEdit: false,
      undoRedoCellEditing: false,
      enterNavigatesVertically: false,
      enterNavigatesVerticallyAfterEdit: false,
      rowSelection: 'multiple',
      defaultColDef: {
        resizable: true,
        menuTabs: [],
        suppressSizeToFit: false
      },
      pinnedBottomRowData: [],
      enableRangeSelection: true,
      animateRows: true,
      suppressRowClickSelection: true,

      // Grouping related
      suppressAggFuncInHeader: true,
      // groupUseEntireRow: true,
      groupSelectsChildren: true,
      // groupIncludeTotalFooter: true,
      autoGroupColumnDef: {
        headerName: 'Vendor',
        minWidth: 200,
        cellRendererParams: {
          suppressCount: true
        }
      },
      aggFuncs: {
        sum: this.customSum, // this overrides the grids built in sum function
        sumSelected: this.customSumSelected
      },

      // Classes and events
      rowClassRules: {
        'ag-error': (params) => params.node.aggData && !this.hasCompleteAchData(params.node.aggData.vendorResource)
      },
      isRowSelectable: (params) => this.isRowSelectable(params),
      onGridReady: (params) => this.onGridReady(params),
      onFirstDataRendered: (params) => this.onFirstDataRendered(params),
      onCellValueChanged: (params) => this.onCellValueChanged(params),
      onSelectionChanged: (params) => {
        this.updateTotals(params.api)
        this.gridApi.refreshClientSideRowModel('aggregate')
      }
    }
  }
})
</script>

<template>
<div>
    <!-- v-if="rowData" -->
  <ag-grid-vue
    :name="name"
    :ref="name"
    :class="classes"
    :style="{ width, height }"
    :key="iteration"
    :groupDefaultExpanded="-1"
    :column-defs="columnDefs"
    :row-data="rowData"
    :grid-options="gridOptions"
  />
</div>
</template>